import { Final, Intro, Question, RaffleForm } from './components';
import styles from './Quiz.module.scss';
import Content from '@ui/components/Content/Content';
import Heading from '@ui/components/Heading/Heading';
import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { getHeadingTag } from '@ui/utils';

const Quiz = ({
  headingLevel = 2,
  description = null,
  finalContent = null,
  finalImage = null,
  finalLink = null,
  finalSubtitle = null,
  formContent = null,
  introButtonText = null,
  introContent = null,
  introImage = null,
  introTitle = null,
  isMobile = false,
  isRaffle = false,
  legalLabel = null,
  legalText = null,
  questions,
  setResult,
  setScore,
  setStep,
  step = null,
  store,
  title = null,
}) => {
  questions = questions || [];
  const topRef = useRef(null);
  const currentStep = isRaffle ? step : step + 1; // if it is not Raffle, it skips the intro

  useEffect(() => {
    if (topRef.current && isMobile) {
      topRef.current.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    }
  }, [step]);

  // Prepare Steps to render

  const questionsToRender = questions?.map((question, index) => ({
    ...question,
    component: Question,
    order: index + 1,
  }));

  const stepToRender = [
    ...questionsToRender,
    {
      component: RaffleForm,
      order: questions.length + 1,
    },
    {
      component: Final,
      order: questions.length + 2,
    },
  ];
  const renderSteps = () => {
    const [CurrentStepComponent] = stepToRender.filter(
      item => item.order === currentStep,
    );

    return CurrentStepComponent ? (
      // eslint-disable-next-line react/jsx-pascal-case
      <CurrentStepComponent.component
        currentQuestionNumber={currentStep}
        finalContent={finalContent}
        finalImage={finalImage}
        finalLink={finalLink}
        finalSubtitle={finalSubtitle}
        formContent={formContent}
        headingLevel={title ? headingLevel + 1 : headingLevel}
        introImage={introImage}
        isRaffle={isRaffle}
        legalLabel={legalLabel}
        legalText={legalText}
        question={questions[currentStep - 1]}
        setResult={setResult || function () {}}
        setScore={setScore || function () {}}
        setStep={setStep || function () {}}
        store={store}
        totalQuestionNumber={questions.length}
      />
    ) : null;
  };

  return (
    <div ref={topRef}>
      {title && (
        <Heading
          align="center"
          as={getHeadingTag(headingLevel)}
          noSpacing
          size="h1"
        >
          {title}
        </Heading>
      )}
      {description && (
        <Content align="center" withParser>
          {description}
        </Content>
      )}
      <div
        className={classNames(styles.widgetContainer, {
          [styles.noDescription]: !description,
        })}
      >
        {isRaffle && step === 0 && (
          <Intro
            headingLevel={title ? headingLevel + 1 : headingLevel}
            introButtonText={introButtonText}
            introContent={introContent}
            introImage={introImage}
            introTitle={introTitle}
            setStep={setStep || function () {}}
          />
        )}
        {renderSteps(step)}
      </div>
    </div>
  );
};

Quiz.propTypes = {
  description: PropTypes.string,
  finalContent: PropTypes.string,
  finalImage: PropTypes.shape({
    title: PropTypes.string,
    url: PropTypes.string,
  }),
  finalLink: PropTypes.shape({
    title: PropTypes.string,
    url: PropTypes.string,
    target: PropTypes.string,
  }),
  finalSubtitle: PropTypes.string,
  formContent: PropTypes.string,
  headingLevel: PropTypes.number,
  introButtonText: PropTypes.string,
  introContent: PropTypes.string,
  introImage: PropTypes.shape({
    title: PropTypes.string,
    url: PropTypes.string,
  }),
  introTitle: PropTypes.string,
  isMobile: PropTypes.bool,
  isRaffle: PropTypes.bool,
  legalLabel: PropTypes.string,
  // questions: PropTypes.arrayOf(
  //   PropTypes.shape({
  //     questionTitle: PropTypes.string,
  //     answers: PropTypes.arrayOf(),
  //     questionDescription: PropTypes.string,
  //     isCheckbox: PropTypes.bool,
  //     questionImage: PropTypes.shape({
  //       title: PropTypes.string,
  //       url: PropTypes.string,
  //     }),
  //   }),
  // ),
  legalText: PropTypes.string,
  questions: PropTypes.arrayOf(PropTypes.shape()),
  setResult: PropTypes.func,
  setScore: PropTypes.func,
  setStep: PropTypes.func,
  step: PropTypes.number,
  /* eslint-disable react/forbid-prop-types */
  /** `useFormStore()` return value from `@ariakit/react` */
  store: PropTypes.object.isRequired,
  title: PropTypes.string,
};

export default Quiz;
